import { Form, Formik, FormikProps, FormikValues } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import Button from '../../../components/buttons/Button';
import { scrollToError } from '../../../utils/HelperFunctions';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generalCreate, generalGet } from '../../../API/api';
import { toast } from 'react-toastify';
import { authContext } from '../../../store/context/authContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IStepsData } from '../types/interfaces';
import * as Yup from 'yup';
import SectionHeader from '../../../components/SectionHeader';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import StepInputs from './StepInputs';
import { SelectChangeEvent } from '@mui/material';

const StepsCreationForm = () => {
  const formRef = useRef(null);
  const innerFormRef = useRef<FormikProps<any>>(null);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { catchError, catchErrorWithToast } = useContext(authContext);
  const [stepData, setStepData] = useState<IStepsData>();
  // const [selectedRoles, setSelectedRoles] = useState<any[]>([]);
  // const [selectedRolesName, setSelectedRolesName] = useState<string[]>([]);
  const [selectedResponsibilities, setSelectedResponsibilities] = useState<any[]>([]);
  const [selectedResponsibilitiesName, setSelectedResponsibilitiesName] = useState<string[]>([]);

  const queryClient = useQueryClient()

  const { data, isSuccess } = useQuery({
    queryKey: ['single step', id],
    queryFn: () => generalGet(`/admin/order-steps/${id}?relations=roles,responsibilities`),
    enabled: !!id
  });

  const {
    data: rolesData,
    isSuccess: rolesIsSuccess
  } = useQuery({
    queryKey: ['roles'],
    queryFn: () => generalGet(`/select/roles`)
  });


  useEffect(() => {
    if (isSuccess) {
      const reqData = data?.data?.data;
      if (reqData) {
        setStepData(reqData);
      }
    }
  }, [isSuccess, data, id]);

  const normalizeValidation = (validationObj: Record<string, any>): string[] => {
    const result: string[] = [];

    if (!validationObj || typeof validationObj !== 'object') return result;

    Object.entries(validationObj).forEach(([key, value]) => {
      if (typeof value === 'boolean' && value) {
        result.push(key); // e.g., "required"
      } else if (typeof value === 'number' || typeof value === 'string') {
        result.push(`${key}:${value}`); // e.g., "min:2", "max:100"
      }
    });

    return result;
  };


  const initialValues = {
    ...stepData,
    
    name: {
      ar: stepData?.name?.ar || '',
      en: stepData?.name?.en || ''
    },
    message: {
      ar: stepData?.message?.ar || '',
      en: stepData?.message?.en || ''
    },
    
    order: stepData?.order || '',
    auto_generate_documents: stepData?.auto_generate_documents || false,
    customer_responsible: stepData?.customer_responsible || false,
    visible_to_customer: stepData?.visible_to_customer || false,
    online_order_step: stepData?.online_order_step || false,
    offline_order_step: stepData?.offline_order_step || false,
    
    removed_entities: [],
    removed_fields: [],
    
    entities: stepData?.entities?.map((entity: any) => ({
      ...entity,
      fields: entity.fields.map((field: any) => ({
        ...field,
        validation: Array.isArray(field.validation)
          ? field.validation
          : normalizeValidation(field.validation),
      }))
    })) || [
      {
        name: {
          ar: '',
          en: ''
        },
        editable_by_customer: false,
        auto_generate_documents: false,
        fields: [
          {
            name: '',
            type: '',
            label: {
              ar: '',
              en: ''
            },
            validation: [] as string[],
          }
        ]
      }
    ],
    
    responsibilities: stepData?.responsibilities || []
  };

  const arabicRegex = /^[\u0600-\u06FF0-9\s]+$/;
  const englishRegex = /^[A-Za-z0-9\s]+$/;


  const validationSchema = Yup.object().shape({
    name: Yup.object().shape({
      ar: Yup.string()
        .required(t('name_ar_required'))
        .matches(arabicRegex, t('name_arabic_only')),
      en: Yup.string()
        .required(t('name_en_required'))
        .matches(englishRegex, t('name_english_only')),
    }),
    message: Yup.object().shape({
      ar: Yup.string()
        .required(t('message_ar_required'))
        .matches(arabicRegex, t('message_arabic_only')),
      en: Yup.string()
        .required(t('message_en_required'))
        .matches(englishRegex, t('message_english_only')),
    }),
    order: Yup.string()
      .required(t('order_required')),

    auto_generate_documents: Yup.boolean(),
    customer_responsible: Yup.boolean(),
    visible_to_customer: Yup.boolean(),
    online_order_step: Yup.boolean(),
    offline_order_step: Yup.boolean(),
    auto_submitted: Yup.boolean(),

    entities: Yup.array().of(
      Yup.object().shape({
        name: Yup.object().shape({
          ar: Yup.string()
            .required(t('entity_name_ar_required'))
            .matches(arabicRegex, t('name_arabic_only')),
          en: Yup.string()
            .required(t('entity_name_en_required'))
            .matches(englishRegex, t('name_english_only')),
        }),
        fields: Yup.array()
          .of(
            Yup.object().shape({
              name: Yup.string()
                .required(t('field_name_required')),
              type: Yup.string()
                .required(t('field_type_required')),
              label: Yup.object().shape({
                ar: Yup.string()
                  .required(t('label_ar_required'))
                  .matches(arabicRegex, t('label_arabic_only')),
                en: Yup.string()
                  .required(t('label_en_required'))
                  .matches(englishRegex, t('label_english_only')),
              }),
              validation: Yup.array().of(Yup.string()),
            })
          )
          .min(1, t('at_least_one_field_required')),
      })
    ),

    // roles: Yup.array().of(Yup.string()).min(1, t('at_least_one_role_required')),
    // responsibilities: Yup.array().of(Yup.string()).min(1, t('at_least_one_responsibility_required')),
  });


  useEffect(() => {
    if (id && stepData && rolesData?.data?.data) {
      // const allowedRolesValues = Array.isArray(stepData.roles)
      //   ? stepData.roles.map((role: { name: string }) => role.name)
      //   : [];

      const allowedResponsibilitiesValues = Array.isArray(stepData.responsibilities)
        ? stepData.responsibilities.map((responsibility: { name: string }) => responsibility.name)
        : [];

      // const filteredRoles = rolesData?.data?.data.filter((role: { value: string }) =>
      //   allowedRolesValues.includes(role.value)
      // );
      const filteredResponsibilities = rolesData?.data?.data.filter((responsibility: { value: string }) =>
        allowedResponsibilitiesValues.includes(responsibility.value)
      );

      // const rolesValues = filteredRoles.map((role: { value: string }) => role.value);
      const responsibilitiesValues = filteredResponsibilities.map((responsibility: { value: string }) => responsibility.value);

      // Set both state variables
      // setSelectedRoles(rolesValues);
      setSelectedResponsibilities(responsibilitiesValues);

      // const selectedRolesIds = rolesData?.data?.data
      //   .filter((role: { value: any }) => rolesValues.includes(role.value))
      //   .map((role: { id: any }) => role.id);

      const selectedResponsibilitiesIds = rolesData?.data?.data
        .filter((responsibility: { value: any }) => responsibilitiesValues.includes(responsibility.value))
        .map((responsibility: { id: any }) => responsibility.id);

      // Update formik values for both
      // innerFormRef.current?.setFieldValue?.('roles', selectedRolesIds, false);
      innerFormRef.current?.setFieldValue?.('responsibilities', selectedResponsibilitiesIds, false);
    }
  }, [stepData, rolesData, id]);



  // useEffect(() => {
  //   if (rolesData?.data?.data && selectedRoles.length > 0) {
  //     const names = rolesData?.data?.data
  //       .filter((role: { value: string }) => selectedRoles.includes(role.value))
  //       .map((role: { text: string }) => role.text);

  //     setSelectedRolesName(names);
  //   }
  // }, [rolesData, selectedRoles]);

  useEffect(() => {
    if (rolesData?.data?.data && selectedResponsibilities.length > 0) {
      const names = rolesData?.data?.data
        .filter((responsibility: { value: string }) => selectedResponsibilities.includes(responsibility.value))
        .map((responsibility: { text: string }) => responsibility.text);

      setSelectedResponsibilitiesName(names);
    }
  }, [rolesData, selectedResponsibilities]);

  // If We Need To Return Roles Back Uncomment This Code
  // const handleRoleChange = (event: SelectChangeEvent<any[]>, formik: any) => {
  //   const value = event.target.value;

  //   // Ensure array of role.values
  //   const newSelected = typeof value === 'string' ? value.split(',') : value;

  //   // Update selectedRoles with values (not ids)
  //   setSelectedRoles(newSelected);

  //   // Update names for UI
  //   const names = rolesData?.data?.data
  //     .filter((role: { value: string }) => newSelected.includes(role.value))
  //     .map((role: { text: string }) => role.text);

  //   setSelectedRolesName(names);

  //   // Send ids to formik (optional for form submission)
  //   const selectedIds = rolesData?.data?.data
  //     .filter((role: { value: any }) => newSelected.includes(role.value))
  //     .map((role: { id: any }) => role.id);

  //   formik.setFieldValue('roles', selectedIds);
  // };

  const handleResponsibilityChange = (event: SelectChangeEvent<any[]>, formik: any) => {
    const value = event.target.value;

    // Ensure array of role.values
    const newSelected = typeof value === 'string' ? value.split(',') : value;

    // Update selectedRoles with values (not ids)
    setSelectedResponsibilities(newSelected);

    // Update names for UI
    const names = rolesData?.data?.data
      .filter((responsibility: { value: string }) => newSelected.includes(responsibility.value))
      .map((responsibility: { text: string }) => responsibility.text);

    setSelectedResponsibilitiesName(names);

    // Send ids to formik (optional for form submission)
    const selectedIds = rolesData?.data?.data
      .filter((responsibility: { value: any }) => newSelected.includes(responsibility.value))
      .map((responsibility: { id: any }) => responsibility.id);

    formik.setFieldValue('responsibilities', selectedIds);
  };


  return (
    <div className="basic_info creation_step" ref={formRef}>
      <Formik
        innerRef={innerFormRef}
        enableReinitialize
        validateOnMount={!id}
        validateOnBlur={id ? true : false}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={(values, { setErrors }) => {
          values.entities.forEach((entity: { fields: { validation: string[]; min: null; max: null; }[]; }) => {
            entity.fields.forEach((field: { validation: string[]; min: null; max: null; }) => {
              const filteredArr = field?.validation?.filter((item: string) => !item.includes('min:') && !item.includes('max:'));
              field.validation = filteredArr;
              field.min && field.min != null && field.validation.push(`min:${field.min}`);
              field.max && field.max != null && field.validation.push(`max:${field.max}`);
            });
          });

          setLoading(true);
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          let route = !id ? `/admin/order-steps` : `/admin/order-steps/${id}`;
          generalCreate({
            route: route,
            values: { ...value }
          })
            .then((res) => {
              setLoading(false);
              toast.success(`${t('step')} ${!id ? t('created') : t('updated')} ${t('successfully')}`);
              queryClient.invalidateQueries({ queryKey: ['order-steps'] });
              navigate(`/order-steps`);
            })
            .catch((error: any) => {
              // catchError(error, setLoading);
              catchErrorWithToast(error, setLoading, setErrors);
            });
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('stepInfo')} customStyle="noBorder noMargin">
                <FieldWrapper>
                  <div className={`input-wrapper start-times`}>
                    <StepInputs
                      formik={formik}
                      // handleRoleChange={handleRoleChange}
                      // selectedRoles={selectedRoles}
                      // selectedRolesName={selectedRolesName}
                      handleResponsibilityChange={handleResponsibilityChange}
                      selectedResponsibilities={selectedResponsibilities}
                      selectedResponsibilitiesName={selectedResponsibilitiesName}
                      rolesData={rolesData?.data?.data}
                    />
                  </div>
                </FieldWrapper>
              </SectionHeader>

              <div className="form_button reverse">
                <Button
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                  text={t('submit')}
                  type={'submit'}
                  disabled={loading}
                  loading={loading}
                ></Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default StepsCreationForm;
